import { useEffect, useRef, useState, useCallback } from "react";
import { Radio, Clock, Filter, Lock, Cpu } from "lucide-react";
import { Link } from "react-router-dom";
import { api, WS_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EventCard from "@/components/EventCard";
import { WhatChanged } from "@/components/WhatChanged";
import PreGameWidget from "@/components/PreGameWidget";
import { CATEGORY_META, LEAGUE_COLORS } from "@/components/badges";
import { Button } from "@/components/ui/button";

const LEAGUES = ["ALL", "NFL", "NBA", "NHL", "MLB"];

export default function LiveFeed() {
  const { user } = useAuth();
  const isPro = !!(user && (["all_star", "founder"].includes(user.tier) || user.role === "admin"));
  const [events, setEvents] = useState([]);
  const [league, setLeague] = useState("ALL");
  const [category, setCategory] = useState("all");
  const [delayed, setDelayed] = useState(false);
  const [delaySecs, setDelaySecs] = useState(0);
  const [live, setLive] = useState(false);
  const [tel, setTel] = useState(null);
  const [wsNonce, setWsNonce] = useState(0);
  const wsRef = useRef(null);
  const newIds = useRef(new Set());

  const load = useCallback(async () => {
    const params = {};
    if (league !== "ALL") params.league = league;
    if (category !== "all") params.category = category;
    const { data } = await api.get("/events", { params });
    setEvents(data.events);
    setDelayed(data.delayed);
    setDelaySecs(data.delay_seconds || 0);
  }, [league, category]);

  useEffect(() => { load(); }, [load]);

  // WebSocket for pro/admin real-time (auto-reconnects on unexpected drop)
  useEffect(() => {
    if (!isPro) { setLive(false); return; }
    let closedByUs = false;
    let retry;
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    ws.onopen = () => setLive(true);
    ws.onclose = () => {
      setLive(false);
      if (!closedByUs) retry = setTimeout(() => setWsNonce((n) => n + 1), 3000);
    };
    ws.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data);
        if (data.type === "telemetry") setTel(data.telemetry);
        if (data.type === "new_event") {
          const ev = data.event;
          if (league !== "ALL" && ev.league !== league) return;
          if (category !== "all" && ev.category !== category) return;
          newIds.current.add(ev.id);
          setEvents((prev) => {
            const next = [ev, ...prev].slice(0, 80);
            const keep = new Set(next.map((e) => e.id));
            newIds.current.forEach((id) => { if (!keep.has(id)) newIds.current.delete(id); });
            return next;
          });
        }
      } catch { /* ignore malformed ws frame */ }
    };
    return () => { closedByUs = true; clearTimeout(retry); ws.close(); };
  }, [isPro, league, category, wsNonce]);

  // Free tier polls periodically
  useEffect(() => {
    if (isPro) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [isPro, load]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-4 sm:p-6 max-w-[1600px] mx-auto">
      {/* Main feed */}
      <div className="lg:col-span-8 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-100">Live Status Feed</h1>
              {live ? (
                <span data-testid="live-indicator" className="inline-flex items-center gap-1.5 rounded-full bg-[#FF4D4D]/15 border border-[#FF4D4D]/40 px-2.5 py-1 text-xs font-mono uppercase tracking-wider text-[#FF7A7A]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#FF4D4D] sq-pulse" /> Live
                </span>
              ) : delayed ? (
                <span data-testid="delayed-indicator" className="inline-flex items-center gap-1.5 rounded-full bg-[#FFB020]/15 border border-[#FFB020]/40 px-2.5 py-1 text-xs font-mono uppercase tracking-wider text-[#FFB020]">
                  <Clock size={11} /> Delayed ~{delaySecs >= 60 ? `${Math.round(delaySecs / 60)}m` : `${delaySecs}s`}
                </span>
              ) : null}
            </div>
            <p className="text-sm text-slate-400 mt-1">Injuries, trades, suspensions & lineup changes — source-cited.</p>
          </div>
        </div>

        <WhatChanged />

        {/* Upgrade banner for delayed (Rookie/Starter) users */}
        {!isPro && (
          <div data-testid="upgrade-banner" className="rounded-xl border border-[#00E5A3]/30 bg-[#00E5A3]/8 p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Lock size={18} className="text-[#00E5A3]" />
              <p className="text-sm text-slate-200">
                You're on the {user?.tier === "starter" ? "5-minute" : "30-minute"} delayed feed. <span className="text-slate-400">Go All-Star to get every update the moment it lands — plus priority alerts on the players you follow.</span>
              </p>
            </div>
            <Button asChild className="bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold h-9 rounded-lg">
              <Link to="/pro" data-testid="banner-upgrade-btn">Go All-Star</Link>
            </Button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {LEAGUES.map((l) => (
            <button key={l} data-testid={`league-filter-${l.toLowerCase()}`} onClick={() => setLeague(l)}
              className={`px-3 py-1.5 rounded-lg text-sm font-mono font-semibold transition-colors border ${league === l ? "text-[#04140F] border-transparent" : "text-slate-300 border-[#232D42] hover:bg-[#151B2B]"}`}
              style={league === l ? { background: l === "ALL" ? "#00E5A3" : LEAGUE_COLORS[l] } : {}}>
              {l}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-[#232D42]" />
          <div className="flex items-center gap-1.5 text-slate-500"><Filter size={14} /></div>
          <select data-testid="category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
            className="bg-[#151B2B] border border-[#232D42] rounded-lg px-3 py-1.5 text-sm text-slate-200 font-mono">
            <option value="all">All categories</option>
            {Object.entries(CATEGORY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        <div className="space-y-3" data-testid="events-list">
          {events.length === 0 && <div className="text-center py-16 text-slate-500 font-mono text-sm">No events match these filters.</div>}
          {events.map((ev) => <EventCard key={ev.id} event={ev} isNew={newIds.current.has(ev.id)} />)}
        </div>
      </div>

      {/* Sidebar */}
      <div className="lg:col-span-4 space-y-6">
        <PreGameWidget />
        <div className="bg-[#0F1420] border border-[#232D42] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Cpu size={16} className="text-[#00E5A3]" />
            <h3 className="font-heading font-semibold text-slate-100">Pipeline Status</h3>
          </div>
          <PipelineMini tel={tel} />
          <Link to="/pipeline" data-testid="view-telemetry-link" className="text-xs text-[#00E5A3] hover:underline mt-4 inline-block font-mono">View full telemetry →</Link>
        </div>

        <div className="bg-[#0F1420] border border-[#232D42] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3"><Radio size={16} className="text-[#38BDF8]" /><h3 className="font-heading font-semibold text-slate-100">How to read events</h3></div>
          <ul className="space-y-2.5 text-sm text-slate-400">
            <li><span className="text-[#38BDF8] font-mono font-semibold">OFFICIAL</span> — league / team confirmed.</li>
            <li><span className="text-[#A855F7] font-mono font-semibold">REPORTED</span> — media / social, unconfirmed.</li>
            <li>Every card shows its <span className="text-slate-200">source</span> & <span className="text-slate-200">last-updated time</span>.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function PipelineMini({ tel }) {
  const [snap, setSnap] = useState(tel);
  useEffect(() => {
    if (tel) { setSnap(tel); return; }
    let active = true;
    const load = async () => { try { const r = await api.get("/pipeline"); if (active) setSnap(r.data); } catch { /* pipeline poll failed; keep last snapshot */ } };
    load();
    const t = setInterval(load, 8000);
    return () => { active = false; clearInterval(t); };
  }, [tel]);

  if (!snap) return <div className="text-sm text-slate-500 font-mono">Loading…</div>;
  return (
    <div className="space-y-2.5">
      {snap.stages.map((s) => (
        <div key={s.key} data-testid={`pipeline-stage-${s.key}`} className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${s.status === "active" ? "bg-[#00E5A3] sq-pulse" : "bg-slate-600"}`} />
            <span className="text-sm text-slate-300 capitalize">{s.key.replace("_", " ")}</span>
          </div>
          <div className="font-mono text-xs text-slate-500">
            <span className="text-slate-300">{s.processed}</span> · {s.avg_latency_ms}ms
          </div>
        </div>
      ))}
      <div className="pt-2 mt-2 border-t border-[#232D42] flex items-center justify-between">
        <span className="text-xs font-mono text-slate-500">Total events</span>
        <span className="font-mono text-sm text-[#00E5A3] font-bold">{snap.total_events}</span>
      </div>
    </div>
  );
}
